import {
  AllActions,
  AllCrudActions,
  EPermissionActions,
} from "./EPermissionActions";
import {
  EPermissionResources as EPermissionResourcesObj,
  type EPermissionResources as EPermissionResourcesType,
} from "./EPermissionResources";

export const ECompanyRoleCode = {
  CompanyAdmin: "company_admin",
  PayrollAdmin: "payroll_admin",
  HRAdmin: "hr_admin",
  FinanceAdmin: "finance_admin",
  ReportsViewer: "reports_viewer",
  L1SupportAgent: "l1_support_agent",
  L2SupportSpecialist: "l2_support_specialist",
  L3SupportAdmin: "l3_support_admin",
} as const;

export type ECompanyRoleCode =
  (typeof ECompanyRoleCode)[keyof typeof ECompanyRoleCode];

export type TRbacRolePermissions = Partial<
  Record<EPermissionResourcesType, number>
>;

/**
 * Static role-definition map for RBAC V1. Includes Company Admin, Payroll Admin, HR Admin,
 * Finance Admin, Reports Viewer, and Support tiers (L1/L2/L3). Used by permission engine
 * to resolve effective permissions from role assignments.
 */
export const RBAC_ROLE_PERMISSIONS: Record<
  ECompanyRoleCode,
  TRbacRolePermissions
> = {
  [ECompanyRoleCode.CompanyAdmin]: {
    [EPermissionResourcesObj.AccrualBalances]: AllActions,
    [EPermissionResourcesObj.AccrualPolicies]: AllActions,
    [EPermissionResourcesObj.CompanyMembers]: AllActions,
    [EPermissionResourcesObj.CompanySettings]: AllActions,
    [EPermissionResourcesObj.ContractorPayments]: AllActions,
    [EPermissionResourcesObj.Contractors]: AllActions,
    [EPermissionResourcesObj.Deductions]: AllActions,
    [EPermissionResourcesObj.Employees]: AllActions,
    [EPermissionResourcesObj.EmployeeChecks]: AllActions,
    [EPermissionResourcesObj.InviteMembers]: AllActions,
    [EPermissionResourcesObj.Locations]: AllActions,
    [EPermissionResourcesObj.Onboarding]: AllActions,
    [EPermissionResourcesObj.PartnerMembership]: AllActions,
    [EPermissionResourcesObj.Payroll]: AllActions,
    [EPermissionResourcesObj.Reports]: AllActions,
    [EPermissionResourcesObj.Shifts]: AllActions,
    [EPermissionResourcesObj.Taxes]: AllActions,
  },
  [ECompanyRoleCode.PayrollAdmin]: {
    [EPermissionResourcesObj.ContractorPayments]: AllActions,
    [EPermissionResourcesObj.Contractors]: EPermissionActions.Read,
    [EPermissionResourcesObj.Employees]: EPermissionActions.Read,
    [EPermissionResourcesObj.Payroll]: AllActions,
    [EPermissionResourcesObj.Reports]: EPermissionActions.Read,
    [EPermissionResourcesObj.Taxes]: EPermissionActions.Read,
  },
  [ECompanyRoleCode.HRAdmin]: {
    [EPermissionResourcesObj.Contractors]: AllCrudActions,
    [EPermissionResourcesObj.Employees]: AllCrudActions,
    [EPermissionResourcesObj.Onboarding]: AllActions,
  },
  [ECompanyRoleCode.FinanceAdmin]: {
    [EPermissionResourcesObj.Contractors]: EPermissionActions.Read,
    [EPermissionResourcesObj.Employees]: EPermissionActions.Read,
    [EPermissionResourcesObj.Reports]: EPermissionActions.Read,
    [EPermissionResourcesObj.Taxes]: EPermissionActions.Read,
  },
  [ECompanyRoleCode.ReportsViewer]: {
    [EPermissionResourcesObj.Reports]: EPermissionActions.Read,
  },
  [ECompanyRoleCode.L1SupportAgent]: {},
  [ECompanyRoleCode.L2SupportSpecialist]: {},
  [ECompanyRoleCode.L3SupportAdmin]: {},
};
